import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, InputAdornment, Popover, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions'
import { SocialCommentTable } from '../social-media/SocialCommentTable'
import { SOCIAL_COLOR_MAP } from '../social-media/design-tokens'
import { HERO_ACCENT } from './HeroStat'
import type { AutoCoderFramework, AutoCoderPipelineRecord } from '../../types/gotypes'

/**
 * Emoji are a first-class search term on this dashboard, not decoration: a
 * comment's reaction is often its whole content, and "find me the 💀 comments"
 * is a question Sky actually asks. The picker exists because those characters
 * are awkward to type on a desktop keyboard.
 */
const COMMON_EMOJIS = [
  '😂', '🤣', '😭', '💀', '🔥', '❤️', '😍', '🥰',
  '💯', '👏', '🙌', '👍', '👎', '😡', '😤', '🤔',
  '😅', '😆', '🤷', '💪', '🎉', '🤯', '😱', '😳',
  '🤮', '🤢', '💩', '🤡', '👀', '✨', '⚡', '🌟',
]

/** How long to wait after a keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 300

export interface CommentStreamProps {
  comments: AutoCoderPipelineRecord[]
  frameworksMap: Record<string, AutoCoderFramework[]>
  totalCount: number
  page: number
  rowsPerPage: number
  /** Debounced — fires SEARCH_DEBOUNCE_MS after typing stops. */
  onSearchChange: (text: string) => void
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rows: number) => void
  /** Table height in px. */
  height?: number
}

/**
 * The searchable stream of coded comments.
 *
 * IT OWNS THE INPUT'S TEXT, NOT THE SEARCH. The box has to re-render on every
 * keystroke to feel alive, but re-querying on every keystroke would fire a
 * request per character; so the typed value is local state and the debounced
 * value goes to the caller. The original page did this with a `setTimeout`
 * returned from the change handler — which never actually cancelled anything,
 * because React does not call the return value of an onChange. The timer here
 * is held in a ref and genuinely cleared, so a fast typist produces one request
 * rather than one per character.
 */
export const CommentStream: React.FC<CommentStreamProps> = ({
  comments,
  frameworksMap,
  totalCount,
  page,
  rowsPerPage,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  height = 500,
}) => {
  const [text, setText] = useState('')
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLButtonElement | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Lifecycle cleanup: a pending search must not fire after unmount.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const pushSearch = useCallback(
    (value: string) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => onSearchChange(value), SEARCH_DEBOUNCE_MS)
    },
    [onSearchChange],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setText(e.target.value)
      pushSearch(e.target.value)
    },
    [pushSearch],
  )

  // Clicking an emoji is an explicit act, so it searches immediately rather
  // than waiting out a debounce meant for typing.
  const handleEmoji = useCallback(
    (emoji: string) => {
      const next = text + emoji
      setText(next)
      if (timer.current) clearTimeout(timer.current)
      onSearchChange(next)
      setEmojiAnchor(null)
    },
    [text, onSearchChange],
  )

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search comments… (use emojis to filter by reaction)"
          value={text}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'grey.400' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'grey.50',
              '& fieldset': { borderColor: 'grey.200' },
              '&:hover fieldset': { borderColor: 'grey.300' },
            },
          }}
        />
        <IconButton
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
          sx={{ bgcolor: 'grey.100', borderRadius: 1, '&:hover': { bgcolor: 'grey.200' } }}
        >
          <EmojiEmotionsIcon sx={{ color: HERO_ACCENT }} />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.500', mb: 1, fontWeight: 500 }}>
            Common Emojis
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5, maxWidth: 280 }}>
            {COMMON_EMOJIS.map((emoji) => (
              <Button
                key={emoji}
                onClick={() => handleEmoji(emoji)}
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  p: 0,
                  fontSize: '1.25rem',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                {emoji}
              </Button>
            ))}
          </Box>
        </Box>
      </Popover>

      <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
        <SocialCommentTable
          data={comments}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          frameworksMap={frameworksMap}
          colorMap={SOCIAL_COLOR_MAP}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Box>
    </Box>
  )
}

export default CommentStream
